import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActionButtonComponent } from '../action-button/action-button.component';

export interface TemplateData {
  id: string;
  name: string;
  pages: number;
  image: string;
  selected: boolean;
}

/**
 * Card component that displays a template preview and exposes actions to apply or preview that template.
 * @component
 * @selector app-template-card
 * @example
 *   <app-template-card [template]="selectedTemplate" [isLoading]="false" (templateSelect)="onSelect($event)" (templatePreview)="onPreview($event)"></app-template-card>
 * @input {TemplateData} template - The template data to display inside the card. When changed, the component responds in ngOnChanges.
 * @input {boolean} isLoading - Flag indicating whether the template content is in a loading state; used to show loading indicators.
 * @output {EventEmitter<TemplateData>} templateSelect - Emitted when the user selects/applies the template (triggered by onApply).
 * @output {EventEmitter<TemplateData>} templatePreview - Emitted when the user requests a preview of the template (triggered by onPreview).
 *
 * Key properties:
 * - eyeIconSrc: string - Path to the eye icon asset used for the preview button.
 *
 * Key methods:
 * - onApply(): void - Handles the apply/select action for the current template and emits templateSelect with the template payload. Also logs the selection for debugging.
 * - onPreview(): void - Handles the preview action and emits templatePreview with the template payload.
 *
 * Lifecycle hooks:
 * - ngOnChanges(changes: SimpleChanges): void - Watches for changes to @Input properties (notably template) and logs changes to the template for debugging purposes.
 *
 * Notes:
 * - This is a standalone component that imports CommonModule and an ActionButtonComponent for UI controls.
 * - Consumers should provide a TemplateData object to the template input and listen to templateSelect and templatePreview to react to user actions.
 */
@Component({
  selector: 'app-template-card',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent],
  templateUrl: './template-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./template-card.component.scss'],
})
export class TemplateCardComponent implements OnChanges {
  @Input() template!: TemplateData;
  @Input() isLoading = false;
  @Output() templateSelect = new EventEmitter<TemplateData>();
  @Output() templatePreview = new EventEmitter<TemplateData>();

  // Eye icon path for preview button
  eyeIconSrc = 'assets/images/eye.svg';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['template']) {
      console.log('Template changed:', this.template);
    }
  }

  onApply(): void {
    console.log('Template selected:', this.template);
    this.templateSelect.emit(this.template);
  }

  onPreview(): void {
    this.templatePreview.emit(this.template);
  }
}
